import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as Croppie from 'croppie';
import { CropData, CroppieOptions, ResultOptions } from 'croppie';

export type Type = 'canvas' | 'base64' | 'html' | 'blob' | 'rawcanvas' | 'points';

@Component({
  selector: 'curbnturf-cropping',
  templateUrl: './cropping.component.html',
  styleUrls: ['./cropping.component.scss'],
  standalone: true,
})
export class CroppingComponent implements OnDestroy, OnInit {
  @ViewChild('imageEdit', { static: true }) imageEdit: ElementRef;
  @Input() croppieOptions: CroppieOptions;

  _imageUrl: string;
  @Input() set imageUrl(url: string) {
    if (url !== this._imageUrl) {
      this.attach(url);
    }
    this._imageUrl = url;
  }

  @Input() points: number[];
  @Input() outputFormatOptions: ResultOptions;
  defaultOutputFormatOptions: ResultOptions = {
    type: 'points' as any,
    size: 'viewport',
    format: 'png',
    quality: 1,
    circle: false,
  };

  @Output() result: EventEmitter<
    string | HTMLElement | Blob | HTMLCanvasElement | { blob: Blob; points: number[]; ratio: number }
  > = new EventEmitter<string | HTMLElement | Blob | HTMLCanvasElement>();
  private _croppie: Croppie;

  ngOnInit(): void {
    const el = this.imageEdit.nativeElement;
    const options = { ...this.croppieOptions };
    if (!options.viewport && el && el.offsetParent) {
      options.viewport = {
        width: el.offsetWidth * 0.9,
        height: el.offsetParent.offsetHeight * 0.9,
      };
    }

    this._croppie = new Croppie(el, options);

    if (this._imageUrl) {
      this.attach(this._imageUrl);
    }
  }

  ngOnDestroy() {
    this._croppie.destroy();
  }

  newResult() {
    this._croppie
      .result({
        ...this.defaultOutputFormatOptions,
        ...this.outputFormatOptions,
      })
      .then((res: any) => {
        this.result.emit({ blob: this._imageUrl, ...res });
      });
  }

  async attach(imageUrl: string) {
    if (this._croppie) {
      await this._croppie.bind({ url: imageUrl, points: this.points });

      // this seems a bit hacky but I can't seem to find a way to get the minimum zoom any other way
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this._croppie.setZoom((this._croppie as any).elements.zoomer.min);
    }
  }

  rotate(degrees: 90 | 180 | 270 | -90 | -180 | -270) {
    this._croppie.rotate(degrees);
  }

  setZoom(zoom: number) {
    this._croppie.setZoom(zoom);
  }

  get(): CropData {
    return this._croppie.get();
  }

  zoomOut() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._croppie.setZoom((this._croppie as any)._currentZoom * 0.75);
  }

  zoomIn() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._croppie.setZoom((this._croppie as any)._currentZoom / 0.75);
  }
}
